import { useEffect, useState, useCallback, useRef } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { AlertCircle, GripVertical, SlidersHorizontal, Search, Copy } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { ErrorView } from "@/components/StateViews";
import { KanbanSkeleton } from "@/components/Skeletons";
import { QualityChip } from "@/components/Chips";
import FilterChips from "@/components/FilterChips";
import FilterPanel from "@/components/FilterPanel";
import DateChips, { computeDateRange, detectDateChip } from "@/components/DateChips";
import LeadDrawer from "@/components/LeadDrawer";
import ChangeStageModal from "@/components/ChangeStageModal";
import { useAuth } from "@/contexts/AuthContext";
import { stageColor } from "@/lib/stageColors";
import { isOverdue, fmtRelative, initials, ageSince, fmtINR } from "@/lib/format";

// Compact service tags for cards/rows: up to 2 + "+X". Accepts string or array.
function serviceTags(value) {
  if (!value) return [];
  const arr = Array.isArray(value) ? value : String(value).split(",").map((s) => s.trim()).filter(Boolean);
  return arr;
}

const COL_PAGE = 60; // cards rendered per column before "show more"

const STAGE_ACCENT = {  "New Lead": "border-t-sky-400", "Call Not Picked": "border-t-amber-400",
  "Call Back Later": "border-t-orange-400", Qualified: "border-t-indigo-400",
  "Meeting Scheduled": "border-t-cyan-400", "Proposal Sent": "border-t-violet-400",
  Negotiation: "border-t-fuchsia-400", Won: "border-t-emerald-500",
  Lost: "border-t-red-400", Junk: "border-t-slate-300", Nurture: "border-t-teal-400",
};

// Same filter set as the Leads list (minus Stage — columns already ARE stages).
const FILTER_KEYS = [
  "quality", "owner_user_id", "service_interested", "budget_range",
  "business_segment", "business_category", "platform", "campaign_name",
  "created_from", "created_to", "follow_up_status", "assigned",
];
const PIPELINE_FILTER_FIELDS = [
  "quality", "service_interested", "budget_range", "business_segment",
  "business_category", "owner_user_id", "assigned", "follow_up_status",
  "platform", "campaign_name", "created",
];
const blankFilters = () => Object.fromEntries(FILTER_KEYS.map((k) => [k, ""]));
const KEY_LABELS = {
  quality: "Quality", owner_user_id: "Owner", service_interested: "Service", budget_range: "Budget",
  business_segment: "Segment", business_category: "Category", platform: "Platform",
  campaign_name: "Campaign", follow_up_status: "Follow-up",
};
const countFilters = (f) => { let n = 0; Object.entries(f).forEach(([k, v]) => { if (!v) return; if (k === "created_to") return; n++; }); return n; };

export default function Pipeline() {
  const { refreshKey, users } = useOutletContext();
  const { options, user } = useAuth();
  const stages = options?.stages || [];
  const [params, setParams] = useSearchParams();

  const initFilters = () => {
    const f = blankFilters();
    FILTER_KEYS.forEach((k) => { const v = params.get(k); if (v) f[k] = v; });
    return f;
  };
  const [search, setSearch] = useState(params.get("search") || "");
  const [applied, setApplied] = useState(initFilters);
  const [draft, setDraft] = useState(applied);
  const [showFilters, setShowFilters] = useState(false);
  const [byStage, setByStage] = useState({});
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [pending, setPending] = useState(null);
  const [subFilters, setSubFilters] = useState({});
  const [statusView, setStatusView] = useState("active");
  const [colLimit, setColLimit] = useState({}); // per-column lazy render limit
  const [dateChip, setDateChip] = useState(() => detectDateChip(initFilters().created_from));
  const scrollRef = useRef(null);
  const autoScroll = useRef({ raf: null, speed: 0 });
  const debounceRef = useRef();

  const stopAutoScroll = useCallback(() => {
    if (autoScroll.current.raf) cancelAnimationFrame(autoScroll.current.raf);
    autoScroll.current.raf = null;
    autoScroll.current.speed = 0;
  }, []);

  const stepScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el && autoScroll.current.speed) {
      el.scrollLeft += autoScroll.current.speed;
      autoScroll.current.raf = requestAnimationFrame(stepScroll);
    } else {
      autoScroll.current.raf = null;
    }
  }, []);

  const onContainerDragOver = useCallback((e) => {
    e.preventDefault();
    const el = scrollRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const edge = 140;
    const x = e.clientX;
    let speed = 0;
    if (x < rect.left + edge) speed = -Math.ceil((edge - (x - rect.left)) / 6);
    else if (x > rect.right - edge) speed = Math.ceil((edge - (rect.right - x)) / 6);
    autoScroll.current.speed = speed;
    if (speed && !autoScroll.current.raf) autoScroll.current.raf = requestAnimationFrame(stepScroll);
    if (!speed) stopAutoScroll();
  }, [stepScroll, stopAutoScroll]);

  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  useEffect(() => {
    const next = {};
    FILTER_KEYS.forEach((k) => { if (applied[k]) next[k] = applied[k]; });
    if (search) next.search = search;
    setParams(next, { replace: true });
  }, [applied, search, setParams]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const cleaned = Object.fromEntries(Object.entries(applied).filter(([, v]) => v));
      const PAGE = 200, MAX_PAGES = 50; // safety cap: up to 10k leads
      const fetchPage = (p) => api.get("/leads", { params: { page: p, page_size: PAGE, view: "board", status_filter: statusView, search: search || undefined, ...cleaned } });
      const first = await minDelay(fetchPage(1));
      const items = [...first.data.data.items];
      const total = first.data.data.total ?? items.length;
      const lastPage = Math.min(MAX_PAGES, Math.ceil(total / PAGE));
      if (lastPage > 1) {
        const rest = await Promise.all(
          Array.from({ length: lastPage - 1 }, (_, i) => fetchPage(i + 2))
        );
        rest.forEach((r) => items.push(...(r.data.data.items || [])));
      }
      const grouped = {};
      stages.forEach((s) => { grouped[s] = []; });
      items.forEach((l) => { (grouped[l.stage] = grouped[l.stage] || []).push(l); });
      setByStage(grouped);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); setLoaded(true); }
  }, [stages.length, applied, search, statusView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!stages.length) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [load, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDraftFilter = (k, v) => {
    if (k === "created_from" || k === "created_to") setDateChip(null);
    setDraft((f) => { const next = { ...f, [k]: v }; if (k === "business_segment") next.business_category = ""; return next; });
  };
  const openFilters = () => { setDraft(applied); setShowFilters(true); };
  const applyFilters = () => { setApplied(draft); setShowFilters(false); };
  const resetDraft = () => setDraft(blankFilters());
  const cancelFilters = () => { setDraft(applied); setShowFilters(false); };
  const applySaved = (saved) => { const f = { ...blankFilters(), ...saved }; setApplied(f); setDraft(f); setDateChip(detectDateChip(f.created_from)); };
  const removeChip = (key) => {
    const next = { ...applied, [key]: "" };
    if (key === "created_from" || key === "created_to") { next.created_from = ""; next.created_to = ""; setDateChip(null); }
    setApplied(next); setDraft(next);
  };
  const clearAllChips = () => { setApplied(blankFilters()); setDraft(blankFilters()); setDateChip(null); };

  // Quick date chips (apply immediately)
  const setDateRange = (days, key) => {
    const { from, to } = computeDateRange(days);
    setApplied((f) => ({ ...f, created_from: from, created_to: to }));
    setDraft((d) => ({ ...d, created_from: from, created_to: to }));
    setDateChip(key);
  };
  const clearDates = () => {
    setApplied((f) => ({ ...f, created_from: "", created_to: "" }));
    setDraft((d) => ({ ...d, created_from: "", created_to: "" }));
    setDateChip(null);
  };
  const hasDateFilter = !!(applied.created_from || applied.created_to);

  const appliedCount = countFilters(applied);
  const draftCount = countFilters(draft);
  const dirty = JSON.stringify(draft) !== JSON.stringify(applied);
  const totalLeads = Object.values(byStage).reduce((acc, arr) => acc + arr.length, 0);

  const chipLabel = (key, val) => {
    if (key === "owner_user_id") return `Owner: ${users?.find((u) => u.id === val)?.name || val}`;
    if (key === "assigned") return val === "true" ? "Assigned" : "Unassigned";
    if (key === "created_from") return `From ${String(val).slice(0, 10)}`;
    if (key === "created_to") return `To ${String(val).slice(0, 10)}`;
    return `${KEY_LABELS[key] || key}: ${val}`;
  };
  const chips = FILTER_KEYS.filter((k) => applied[k]).map((k) => ({ key: k, label: chipLabel(k, applied[k]) }));

  const onDrop = (stage) => {
    stopAutoScroll();
    setDragOver(null);
    const id = dragId;
    setDragId(null);
    if (!id) return;
    let moved, fromStage;
    for (const s of Object.keys(byStage)) {
      const found = byStage[s].find((l) => l.id === id);
      if (found) { moved = found; fromStage = s; break; }
    }
    if (!moved || fromStage === stage) return;
    // Non-optimistic: the card stays in its column. On confirm we reload; on cancel/fail it never moved.
    setPending({ lead: moved, stage });
  };

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col" data-testid="pipeline-page">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-sora text-3xl font-bold text-navy">Pipeline</h1>
          <p className="text-sm text-ink/60">{totalLeads} leads shown · Drag cards between stages to update them.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input data-testid="pipeline-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="h-10 w-52 rounded-xl border border-sky bg-white pl-9 pr-3 text-sm outline-none focus:border-loop" />
          </div>
          <button onClick={() => showFilters ? cancelFilters() : openFilters()} data-testid="pipeline-filter-toggle" className="relative flex h-10 items-center gap-2 rounded-xl border border-sky bg-white px-3 text-sm font-medium text-navy hover:bg-mist">
            <SlidersHorizontal className="h-4 w-4" /> Filters
            {appliedCount > 0 && <span className="rounded-full bg-loop px-1.5 text-[11px] font-bold text-white">{appliedCount}</span>}
          </button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5" data-testid="pipeline-status-tabs">
          {[["active", "Active"], ["all", "All"]].map(([k, label]) => (
            <button key={k} data-testid={`pipeline-status-${k}`} onClick={() => setStatusView(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${statusView === k ? "bg-navy text-white" : "border border-sky bg-white text-ink/60 hover:bg-mist"}`}>
              {label}
            </button>
          ))}
        </div>
        <DateChips selected={dateChip} onPick={setDateRange} onClear={clearDates} hasDates={hasDateFilter} testid="pipeline-date-chips" />
      </div>

      {showFilters && (
        <div className="mb-3">
          <FilterPanel
            module="pipeline"
            fields={PIPELINE_FILTER_FIELDS}
            options={options} users={users} user={user}
            draft={draft} applied={applied}
            appliedCount={appliedCount} draftCount={draftCount} dirty={dirty}
            onChange={setDraftFilter} onApply={applyFilters} onReset={resetDraft} onCancel={cancelFilters} onApplySaved={applySaved}
          />
        </div>
      )}

      {chips.length > 0 && <div className="mb-3"><FilterChips chips={chips} onRemove={removeChip} onClearAll={clearAllChips} testid="pipeline-chips" /></div>}

      <div className="relative flex flex-1 flex-col">
        {loading ? <KanbanSkeleton columns={6} /> : error ? <ErrorView message="Couldn't load the pipeline. Please try again." onRetry={load} /> : (
        <div ref={scrollRef} onDragOver={onContainerDragOver} className="flex flex-1 gap-4 overflow-x-auto pb-4">
          {(statusView === "active" ? stages.filter((s) => !["Junk", "Lost"].includes(s)) : stages).map((stage, ci) => {
            const allItems = byStage[stage] || [];
            const subs = options?.substages_by_stage?.[stage] || [];
            const activeSub = subFilters[stage] || "";
            const items = activeSub ? allItems.filter((l) => l.sub_stage === activeSub) : allItems;
            const limit = colLimit[stage] ?? COL_PAGE;
            const shown = items.slice(0, limit);
            const meta = options?.stage_meta?.[stage];
            const accent = meta?.color ? stageColor(meta.color).accent : (STAGE_ACCENT[stage] || "border-t-slate-300");
            return (
              <div
                key={stage}
                data-testid={`kanban-col-${stage}`}
                style={{ animation: "stageIn 0.4s ease-out both", animationDelay: `${Math.min(ci, 12) * 0.04}s` }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(stage); }}
                onDragLeave={() => setDragOver((s) => (s === stage ? null : s))}
                onDrop={() => onDrop(stage)}
                className={`flex w-[300px] flex-shrink-0 flex-col rounded-2xl border border-t-4 bg-mist/60 ${accent} ${dragOver === stage ? "border-loop bg-sky/30 ring-2 ring-loop/30" : "border-sky/60"}`}
              >
                <div className="flex items-center justify-between px-4 py-3">
                  <h3 className="font-sora text-sm font-semibold text-navy">{meta?.name || stage}</h3>
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-ink/60">{allItems.length}</span>
                </div>
                {subs.length > 0 && (
                  <div className="flex flex-wrap gap-1 px-3 pb-2.5" data-testid={`kanban-subfilter-${stage}`}>
                    <button onClick={() => setSubFilters((f) => ({ ...f, [stage]: "" }))} data-testid={`subchip-all-${stage}`}
                      className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${!activeSub ? "border-loop bg-loop text-white" : "border-sky bg-white text-ink/55 hover:bg-mist"}`}>
                      All {allItems.length}
                    </button>
                    {subs.map((s) => {
                      const on = activeSub === s.key;
                      const cnt = allItems.filter((l) => l.sub_stage === s.key).length;
                      const col = stageColor(s.color);
                      return (
                        <button key={s.key} onClick={() => setSubFilters((f) => ({ ...f, [stage]: on ? "" : s.key }))} data-testid={`subchip-${s.key}`}
                          className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${on ? "border-loop bg-loop text-white" : "border-sky bg-white text-ink/55 hover:bg-mist"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${on ? "bg-white" : col.dot}`} />{s.name} {cnt}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div onScroll={(e) => {
                    const el = e.currentTarget;
                    if (el.scrollHeight - el.scrollTop - el.clientHeight < 240 && shown.length < items.length) {
                      setColLimit((m) => ({ ...m, [stage]: (m[stage] ?? COL_PAGE) + COL_PAGE }));
                    }
                  }} className="flex-1 space-y-2.5 overflow-y-auto px-2.5 pb-3 no-scrollbar">
                  {items.length === 0 && (
                    <div className={`mx-1 mt-2 rounded-xl border border-dashed py-8 text-center text-xs ${dragOver === stage ? "border-loop text-loop" : "border-sky text-ink/35"}`}>
                      {dragOver === stage ? "Drop to move here" : activeSub ? "No leads in this sub-stage" : "No leads in this stage"}
                    </div>
                  )}
                  {shown.map((l) => (
                    <div
                      key={l.id}
                      draggable
                      onDragStart={() => setDragId(l.id)}
                      onDragEnd={() => { setDragId(null); setDragOver(null); stopAutoScroll(); }}
                      onClick={() => setSelected(l.id)}
                      data-testid={`kanban-card-${l.id}`}
                      className={`group cursor-pointer rounded-xl border border-sky/70 bg-white p-3 transition-all hover:-translate-y-0.5 hover:shadow-md ${dragId === l.id ? "opacity-40" : ""} ${pending?.lead?.id === l.id ? "ring-2 ring-loop/40" : ""}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate font-sora text-sm font-semibold text-navy">{l.name || "—"}</p>
                          {l.business_name && <p className="truncate text-xs text-ink/50">{l.business_name}</p>}
                        </div>
                        <GripVertical className="h-4 w-4 flex-shrink-0 text-ink/20 group-hover:text-ink/40" />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <QualityChip value={l.quality} />
                        {l.sub_stage && options?.substage_meta?.[l.sub_stage] && (
                          <span data-testid={`kanban-substage-${l.id}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${stageColor(options.substage_meta[l.sub_stage].color).soft}`}>
                            {options.substage_meta[l.sub_stage].name}
                          </span>
                        )}
                        {l.budget_range && <span className="rounded-full bg-mist px-2 py-0.5 text-[10px] font-medium text-ink/60">{l.budget_range}</span>}
                        {l.possible_duplicate && <span data-testid={`kanban-dup-${l.id}`} title="Possible duplicate lead" className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700"><Copy className="h-2.5 w-2.5" />Possible dup</span>}
                      </div>
                      {(() => { const tags = serviceTags(l.service_interested); return tags.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`kanban-services-${l.id}`}>
                          {tags.slice(0, 2).map((t) => <span key={t} className="rounded-md bg-loop/10 px-1.5 py-0.5 text-[10px] font-medium text-loop">{t}</span>)}
                          {tags.length > 2 && <span className="text-[10px] font-medium text-ink/40">+{tags.length - 2}</span>}
                        </div>
                      ); })()}
                      {l.deal_amount != null && (
                        <p className="mt-1.5 text-[11px] font-semibold text-emerald-600" data-testid={`kanban-deal-${l.id}`}>Deal: {fmtINR(l.deal_amount)}</p>
                      )}
                      {(l.platform || l.campaign_name) && (
                        <p className="mt-1 truncate text-[10px] text-ink/40">{[l.platform, l.campaign_name].filter(Boolean).join(" · ")}</p>
                      )}
                      <div className="mt-2 flex items-center justify-between border-t border-sky/50 pt-2 text-[11px] text-ink/50">
                        <span className="flex items-center gap-1">
                          {l.owner_name ? <span className="flex h-5 w-5 items-center justify-center rounded bg-navy text-[9px] font-bold text-white" title={l.owner_name}>{initials(l.owner_name)}</span> : <span className="text-amber-600">Unassigned</span>}
                          <span className="text-ink/35" title="Lead age">{ageSince(l.created_at)}</span>
                        </span>
                        {l.next_follow_up_at && (
                          <span className={`flex items-center gap-1 ${isOverdue(l.next_follow_up_at) ? "font-semibold text-red-500" : ""}`}>
                            {isOverdue(l.next_follow_up_at) && <AlertCircle className="h-3 w-3" />}{fmtRelative(l.next_follow_up_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  {shown.length < items.length && (
                    <button onClick={() => setColLimit((m) => ({ ...m, [stage]: (m[stage] ?? COL_PAGE) + COL_PAGE }))}
                      data-testid={`kanban-loadmore-${stage}`}
                      className="mx-1 w-[calc(100%-0.5rem)] rounded-lg border border-dashed border-sky py-2 text-xs font-medium text-ink/45 hover:bg-mist hover:text-loop">
                      Show more · {items.length - shown.length} of {items.length} hidden
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
      <LeadDrawer leadId={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} onUpdated={load} users={users} />
      <ChangeStageModal
        open={!!pending}
        onOpenChange={(v) => !v && setPending(null)}
        leadId={pending?.lead?.id}
        lead={pending?.lead}
        presetStage={pending?.stage}
        onDone={() => { setPending(null); load(); }}
      />
    </div>
  );
}

